import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Code,
  Button,
  useToast,
  Link,
} from '@chakra-ui/react'
import { LuMail, LuCheck, LuCopy, LuClock, LuExternalLink, LuTriangleAlert } from 'react-icons/lu'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useQuery } from '@tanstack/react-query'
import axiosClient from '../../api/axios'
import { useTranslation } from 'react-i18next'

interface Ingestion {
  id: string
  sender: string
  subject: string | null
  status:
    | 'processed'
    | 'rejected_unknown_sender'
    | 'rejected_no_attachment'
    | 'rejected_invalid_attachment'
    | 'rejected_signature'
    | 'failed'
  caseId: string | null
  attachmentCount: number
  processedAttachmentCount: number
  errorReason: string | null
  receivedAt: string
}

interface ScanInbox {
  address: string
  recognizedEmail: string
  ingestions: Ingestion[]
}

const STATUS_META: Record<Ingestion['status'], { label: string; scheme: string }> = {
  processed: { label: 'profile.scanInbox.processed', scheme: 'green' },
  rejected_unknown_sender: { label: 'profile.scanInbox.unknownSender', scheme: 'orange' },
  rejected_no_attachment: { label: 'profile.scanInbox.noAttachment', scheme: 'gray' },
  rejected_invalid_attachment: { label: 'profile.scanInbox.rejectedAttachment', scheme: 'gray' },
  rejected_signature: { label: 'profile.scanInbox.invalidSignature', scheme: 'red' },
  failed: { label: 'profile.scanInbox.failed', scheme: 'red' },
}

export default function ScanInboxPanel() {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, isLoading, error } = useQuery({
    queryKey: ['me', 'scan-inbox'],
    queryFn: async () => {
      const { data } = await axiosClient.get<ScanInbox>('/users/me/scan-inbox')
      return data
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: t('profile.scanInbox.copied'), status: 'success', duration: 1500 })
    } catch {
      toast({ title: t('profile.scanInbox.copyFailed'), status: 'error', duration: 2000 })
    }
  }

  return (
    <Box bg="white" borderRadius="2xl" boxShadow="lg" p={{ base: 5, md: 7 }}>
      <HStack spacing={3} mb={1}>
        <Box
          w="44px"
          h="44px"
          borderRadius="xl"
          bgGradient="linear(135deg, teal.100, blue.100)"
          display="grid"
          sx={{ placeItems: 'center' }}
        >
          <Icon as={LuMail} boxSize={5} color="teal.600" />
        </Box>
        <VStack align="start" spacing={0}>
          <Text fontWeight="700" fontSize="lg" color="gray.800">
            Recevoir vos scans par email
          </Text>
          <Text fontSize="xs" color="gray.500">
            Forwardez les scans depuis votre boîte — Careflai crée le cas automatiquement.
          </Text>
        </VStack>
      </HStack>

      <Divider my={4} />

      {isLoading && (
        <Center py={10}>
          <Spinner color="teal.500" />
        </Center>
      )}

      {error && (
        <Text color="red.500" fontSize="sm">
          Erreur de chargement : {(error as Error).message}
        </Text>
      )}

      {data && (
        <>
          <VStack align="stretch" spacing={4}>
            {/* Setup steps */}
            <Box bg="gray.50" borderRadius="xl" p={4}>
              <VStack align="stretch" spacing={3}>
                <HStack spacing={3} align="start">
                  <Badge
                    colorScheme="teal"
                    borderRadius="full"
                    fontSize="xs"
                    minW="22px"
                    h="22px"
                    display="grid"
                    sx={{ placeItems: 'center' }}
                  >
                    1
                  </Badge>
                  <VStack align="stretch" spacing={1} flex={1}>
                    <Text fontSize="sm" color="gray.700" fontWeight="600">
                      Forwardez les scans vers :
                    </Text>
                    <HStack
                      bg="white"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="gray.200"
                      px={3}
                      py={2}
                      spacing={2}
                    >
                      <Code
                        fontSize="sm"
                        bg="transparent"
                        flex={1}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {data.address}
                      </Code>
                      <Button
                        size="xs"
                        leftIcon={<Icon as={LuCopy} />}
                        onClick={() => copy(data.address)}
                        colorScheme="teal"
                        variant="ghost"
                      >
                        Copier
                      </Button>
                    </HStack>
                  </VStack>
                </HStack>

                <HStack spacing={3} align="start">
                  <Badge
                    colorScheme="teal"
                    borderRadius="full"
                    fontSize="xs"
                    minW="22px"
                    h="22px"
                    display="grid"
                    sx={{ placeItems: 'center' }}
                  >
                    2
                  </Badge>
                  <VStack align="stretch" spacing={1} flex={1}>
                    <Text fontSize="sm" color="gray.700" fontWeight="600">
                      Email reconnu pour identifier votre compte :
                    </Text>
                    <HStack
                      bg="white"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="green.200"
                      px={3}
                      py={2}
                      spacing={2}
                    >
                      <Icon as={LuCheck} color="green.500" boxSize={4} />
                      <Code fontSize="sm" bg="transparent" flex={1}>
                        {data.recognizedEmail}
                      </Code>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {t('profile.scanInbox.forwardNotice')}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>

            {/* Tip box */}
            <HStack
              bg="blue.50"
              borderRadius="xl"
              p={3}
              spacing={3}
              align="start"
              border="1px solid"
              borderColor="blue.100"
            >
              <Icon as={LuExternalLink} color="blue.500" boxSize={4} mt="2px" />
              <Text fontSize="xs" color="blue.800" lineHeight="1.55">
                <Text as="span" fontWeight="700">{t('profile.scanInbox.tipLabel')}</Text>{' '}
                {t('profile.scanInbox.tipBody', { address: data.address })}
              </Text>
            </HStack>
          </VStack>

          <Divider my={5} />

          <Text fontWeight="600" fontSize="sm" color="gray.700" mb={3}>
            Historique récent
          </Text>

          {data.ingestions.length === 0 && (
            <Center py={6} bg="gray.50" borderRadius="xl" border="1px dashed" borderColor="gray.200">
              <VStack spacing={1}>
                <Icon as={LuMail} boxSize={6} color="gray.400" />
                <Text fontSize="sm" color="gray.600" fontWeight="600">
                  Aucun email reçu pour l'instant
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Forwardez un scan à l'adresse ci-dessus pour tester.
                </Text>
              </VStack>
            </Center>
          )}

          {data.ingestions.length > 0 && (
            <Box overflowX="auto" border="1px solid" borderColor="gray.100" borderRadius="lg">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th fontSize="2xs">Quand</Th>
                    <Th fontSize="2xs">De</Th>
                    <Th fontSize="2xs">Sujet</Th>
                    <Th fontSize="2xs">Statut</Th>
                    <Th fontSize="2xs">Cas</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.ingestions.map((e) => {
                    const m = STATUS_META[e.status]
                    return (
                      <Tr key={e.id} _hover={{ bg: 'gray.50' }}>
                        <Td fontSize="xs">
                          <HStack spacing={1.5}>
                            <Icon as={LuClock} boxSize={3} color="gray.400" />
                            <Text>
                              {formatDistanceToNow(new Date(e.receivedAt), {
                                addSuffix: true,
                                locale: fr,
                              })}
                            </Text>
                          </HStack>
                        </Td>
                        <Td fontSize="xs" maxW="220px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {e.sender}
                        </Td>
                        <Td fontSize="xs" color="gray.600" maxW="240px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {e.subject || <Text as="span" color="gray.400">—</Text>}
                        </Td>
                        <Td>
                          <Badge colorScheme={m.scheme} variant="subtle" borderRadius="full" px={2} fontSize="2xs">
                            <HStack spacing={1}>
                              {e.status !== 'processed' && <Icon as={LuTriangleAlert} boxSize={3} />}
                              <Text>{t(m.label)}</Text>
                            </HStack>
                          </Badge>
                          {e.errorReason && (
                            <Text fontSize="2xs" color="gray.500" mt={1} maxW="200px">
                              {e.errorReason}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          {e.caseId ? (
                            <Link
                              href={`/lab/blot-analysis/${e.caseId}`}
                              color="teal.600"
                              fontSize="xs"
                              fontWeight="600"
                            >
                              Ouvrir
                            </Link>
                          ) : (
                            <Text fontSize="2xs" color="gray.400">—</Text>
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
